import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import { SuggestNameButton } from '@/components/organisms/FishGroupForms';
import type { OverviewCage } from '@/domain/fishGroups';

import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import { DestinationInflows } from './DestinationInflows';

import styles from '../../operateModals.module.scss';

interface DestinationCardProps {
    index: number;
    destination: WizardDestination;
    sources: WizardSource[];
    locationCages: OverviewCage[];
    dispatch: (action: OperateWizardAction) => void;
}

export function DestinationCard(props: DestinationCardProps) {
    const { index, destination, sources, locationCages, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    const sourceIdsListed = new Set(sources.map((s) => s.fishGroupId));

    return (
        <Card as="article" radius="sm" padding="xs" className={styles.destinationCard}>
            <header className={styles.destinationHeader}>
                <h5 className={styles.destinationTitle}>
                    {t('actions.operate.destinations.destinationLabel', { index: index + 1 })}
                </h5>
                <Button
                    variant="tertiary"
                    size="small"
                    type="button"
                    onClick={() =>
                        dispatch({ type: 'removeDestination', tempId: destination.tempId })
                    }>
                    {t('actions.operate.destinations.removeButton')}
                </Button>
            </header>

            <div className={styles.fieldsGrid}>
                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-name`}
                    label={t('actions.operate.destinations.fields.name')}
                    labelAction={
                        <SuggestNameButton
                            cageName={
                                locationCages.find((c) => c.cageId === destination.cageId)?.cageName
                            }
                            operation="redistribution"
                            onApply={(value) =>
                                dispatch({
                                    type: 'setDestinationName',
                                    tempId: destination.tempId,
                                    value,
                                })
                            }
                        />
                    }>
                    <TextField
                        id={`${destination.tempId}-name`}
                        type="text"
                        placeholder={t('actions.operate.destinations.fields.namePlaceholder')}
                        value={destination.name}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationName',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-cage`}
                    label={t('actions.operate.destinations.fields.cage')}>
                    <Select
                        id={`${destination.tempId}-cage`}
                        value={destination.cageId == null ? '' : String(destination.cageId)}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationCage',
                                tempId: destination.tempId,
                                value: e.target.value === '' ? null : Number(e.target.value),
                            })
                        }>
                        <option value="">
                            {t('actions.operate.destinations.fields.cagePlaceholder')}
                        </option>
                        {locationCages.map((cage) => (
                            <option key={cage.cageId} value={cage.cageId}>
                                {formatCageOption(cage, sourceIdsListed, t)}
                            </option>
                        ))}
                    </Select>
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-count`}
                    label={t('actions.operate.destinations.fields.estimatedCount')}
                    hint={t('actions.operate.destinations.fields.estimatedCountHint')}>
                    <TextField
                        id={`${destination.tempId}-count`}
                        type="number"
                        min={1}
                        value={destination.estimatedFishCount}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationEstimatedCount',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-notes`}
                    label={t('actions.operate.destinations.fields.notes')}
                    className={styles['field--fullWidth']}>
                    <TextArea
                        id={`${destination.tempId}-notes`}
                        rows={2}
                        value={destination.notes}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationNotes',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>
            </div>

            <DestinationInflows destination={destination} sources={sources} dispatch={dispatch} />
        </Card>
    );
}

function formatCageOption(
    cage: OverviewCage,
    sourceIdsListed: Set<number>,
    t: (key: string, options?: Record<string, unknown>) => string
): string {
    if (cage.activeFishGroup === null) {
        return t('actions.operate.destinations.cageOption.empty', { cageName: cage.cageName });
    }
    if (sourceIdsListed.has(cage.activeFishGroup.id)) {
        return t('actions.operate.destinations.cageOption.occupiedBySource', {
            cageName: cage.cageName,
            fishGroupName: cage.activeFishGroup.name,
        });
    }
    return t('actions.operate.destinations.cageOption.occupiedByOther', {
        cageName: cage.cageName,
        fishGroupName: cage.activeFishGroup.name,
    });
}
